import Uppy, { UppyFile } from "@uppy/core";
import AwsS3 from "@uppy/aws-s3";
import { noop } from 'lodash';
import { DiscoverySession } from '../../../stores/jotai/discovery-session';
import { protectedApiCall } from '../../../api/network';
import { toast } from "sonner";
import { MAXIMUM_MEDIA_FILE_SIZE_BYTES, MAXIMUM_MEDIA_FILE_SIZE_MB } from "../../../constants";
import { renderErrorToast } from "../../../../lib/ErrorToastMessage";

const NO_RESERVATION = "none";

export interface IStorageInfo {
    signedRequest: string;
    artifactID: string;
    interviewID: string;
    dataURL: string;
    transcriptArtifactID: string,
}

export const uppyKeyFormatter = (userID: string, file: UppyFile) => {
    return userID + "/" + crypto.randomUUID() + "." + file.extension;
}

export const calculateReservation = (interviewID: string = undefined) => {
    return interviewID || NO_RESERVATION;
};

export const getStorageSpecificInfo = (file: UppyFile) => {
    const { meta, name: fileName, type: fileType } = file;
    const { teamID, interviewID, name: originalName, discoverySession } = meta;

    // @ts-ignore
    const reservation = calculateReservation(interviewID);

    const apiParams = {
        fileName,
        fileType,
        originalName: originalName,
        teamID,
        reservation,
        interviewName: originalName,
    };

    // @ts-ignore
    return protectedApiCall(discoverySession, 'storage/mark/s3', {}, {
        params: apiParams,
    }, 'get');
};

export const createUppyInstance = (
    discoverySession: DiscoverySession,
    teamID: string,
    onGetStorageInfo: Function = noop,
) => {
    let runningUppyCache = undefined;

    try {
        //@ts-ignore
        if (JSON.stringify(window.runningUppyCacheProps) === JSON.stringify({
            discoverySession,
            teamID
        })) {
            //@ts-ignore
            runningUppyCache = window.runningUppyCache;

            // console.log('status: serving cached uppy instance')
        }
    }
    catch (e) {

    }

    if (runningUppyCache !== undefined) {
        return runningUppyCache;
    }

    const uppy = new Uppy({
        debug: false,
        restrictions: {
            maxNumberOfFiles: 50,
            allowedFileTypes: ["video/*", "audio/*"],
        },
        autoProceed: true,
        meta: {
            discoverySession,
            teamID,
        },
        onBeforeFileAdded: (currentFile: UppyFile) => {
            // inject the userID into the filename now so that
            // it maintains consistency for the signature check
            // on AWS S3's side

            //@ts-ignore
            const currentUserID = currentFile.meta.discoverySession.user.userID;

            const fileName = uppyKeyFormatter(currentUserID, currentFile);
            const originalName = currentFile.name;

            const modifiedFile = {
                ...currentFile,
                name: fileName,
                originalName,
            };

            return modifiedFile;
        },
    }).use(AwsS3, {
        // @ts-ignore
        getUploadParameters: (file: UppyFile) => {
            return getStorageSpecificInfo(file).then(
                (storageInfo: IStorageInfo) => {
                    const { id: fileID, meta } = file;
                    const { canEditInterviewDetails } = meta;
                    const {
                        signedRequest: url,
                        artifactID,
                        interviewID,
                        dataURL: s3FileURL,
                        transcriptArtifactID,
                    } = storageInfo;
                    const fileMeta = {
                        ...file.meta,
                        interviewID,
                        artifactID,
                        vultrKey: artifactID,
                        dataURL: artifactID,
                        s3FileURL,
                        transcriptArtifactID,
                    };
                    const fileDetails = {
                        ...file,
                        meta: fileMeta,
                    };

                    onGetStorageInfo(fileDetails, storageInfo);

                    uppy.setFileMeta(fileID, {
                        discoverySession: {}, // cleaning sensitive data before uploading
                        canEditInterviewDetails,
                        interviewID,
                        artifactID,
                        vultrKey: artifactID,
                        dataURL: artifactID,
                        s3FileURL,
                        transcriptArtifactID,
                    });

                    return {
                        method: "put",
                        url,
                        fields: {},
                        headers: {
                            "Content-Type": file.type,
                        },
                    };
                }
            );
        },
    });

    try {
        //@ts-ignore
        window.runningUppyCache = uppy;
        //@ts-ignore
        window.runningUppyCacheProps = {
            discoverySession,
            teamID
        }
    }
    catch (e) {

    }

    return uppy;
};

export const destroyUppyInstance = (uppy: Uppy) => {
    uppy.close({ reason: "unmount" });
    //@ts-ignore
    window.runningUppyCache = undefined;
    //@ts-ignore
    window.runningUppyCacheProps = undefined;
}

export const addFileToUppy = async ({ file, uppy, interviewID,canEditInterviewDetails=false, setFileIdToUpload }) => {
    const uppyFile = {
      source: "discovery",
      name: file.name,
      type: file.type,
      data: file,
      meta: {
        interviewID,
        canEditInterviewDetails: canEditInterviewDetails,
        createNewInterview: false,
        shouldCreateOrUpdateInterview: false,
      },
    };
  
    if (file?.size <= MAXIMUM_MEDIA_FILE_SIZE_BYTES) {
      try {
        const reader = new FileReader();
        reader.onload = () => {
          const audioURL = URL.createObjectURL(file);
          const audioContext = new (window.AudioContext || window?.webkitAudioContext)();
          fetch(audioURL)
            .then((response) => response.arrayBuffer())
            .then((arrayBuffer) => audioContext.decodeAudioData(arrayBuffer))
            .then(() => {
              setFileIdToUpload(uppy.addFile(uppyFile));
            })
            .catch(() => {
                renderErrorToast("Failed to decode audio file.")
            });
        };
        reader.readAsDataURL(file);
      } catch (error) {
        renderErrorToast(error?.message)
      }
    } else {
        renderErrorToast(
        `${file?.name} exceeds the size limit. Please, try to upload file within ${MAXIMUM_MEDIA_FILE_SIZE_MB}MB.`
      );
    }
  };